import { UrlCleaner } from './url_cleaner'

export class Scraper {
  constructor(text, { baseUrl }) {
    this.baseUrl = baseUrl
    this.cleaner = new UrlCleaner(text)
  }

  get isScrapable() {
    return this.cleaner.isValid
  }

  get isInvalid() {
    return !this.isEmpty && !this.cleaner.isValid
  }

  get isEmpty() {
    return this.cleaner.isEmpty
  }

  // The product URL as written: the cleaner percent-encodes it for the query.
  get url() {
    try {
      return decodeURI(this.cleaner.url)
    } catch {
      // The cleaner's regexp can cut an escape in two: send it as it is.
      return this.cleaner.url
    }
  }

  get uri() {
    const uri = new URL(this.baseUrl, document.location)
    uri.searchParams.set('url', this.cleaner.url)

    return uri
  }

  // `html`: the page as the user's phone rendered it (see
  // doc/ajout-de-produit.md) — sent to the server, which parses it instead of
  // fetching the merchant.
  scrap({ html = null } = {}) {
    return new Promise((resolve, reject) => {
      this.#request(html)
        .then(async (response) => {
          if (response.ok) {
            resolve(new Product(await response.json()))
          } else {
            reject(await this.#buildError(response))
          }
        })
        .catch((error) => reject(new ScraperError('error', { cause: error })))
    })
  }

  #request(html) {
    if (!html) return fetch(this.uri)

    return fetch(new URL(this.baseUrl, document.location), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector("meta[name='csrf-token']")?.content ?? '',
      },
      body: JSON.stringify({ url: this.cleaner.url, html }),
    })
  }

  // `status` travels along: after the Cerebro redirect, a non-422 failure carries
  // the merchant's own status (403, 404…), which is not a Listy bug.
  async #buildError(response) {
    switch (response.status) {
      case 422:
        return new ScraperError('unprocessable_content', await response.json())
      default:
        return new ScraperError('error', { status: response.status })
    }
  }
}

class ScraperError extends Error {
  constructor(message, data = {}) {
    super(message)
    this.data = data
  }

  // A 4xx is the merchant (through Cerebro) or our own 422 refusing the page:
  // the user sees the default error, nothing to report.
  get isRefusal() {
    return this.message === 'unprocessable_content' || (this.data.status >= 400 && this.data.status < 500)
  }
}

class Product {
  constructor(json) {
    this.url = json.url || json.canonical_url
    this.name = json.name || json.title || ''
    this.amount = json.amount || json.price || ''
    this.currency = json.currency || ''
    this.pictures = json.pictures || json.product_pictures || []
  }
}
